import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { getAuth, isSuperAdmin, isSuperPfAdmin } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CreditCard, Eye, ChevronLeft, ChevronRight, Loader2,
  CheckCircle2, Clock, DollarSign, FileText, ArrowLeft, AlertTriangle,
  RefreshCw, EyeOff, Send, MoreVertical, AlertOctagon, Upload, Download,
  Info, FileDown, Plus,
} from 'lucide-react';
import PaymentFileDetail from './PaymentFileDetail';
import { NotesIcon } from '../../components/notes/NotesIcon';
import { FundsPulledBreakdownModal } from '../../components/payments/FundsPulledBreakdownModal';

// Feature flag: hide all "Customer" bucket labels (Paid by Customer / Refund to Customer / ↳ to Customer)
// from the org-level Payments accordion. Backend totals are unchanged.
// Flip back to `false` to restore the rows. See also PaymentFileDetail.js.
const HIDE_CUSTOMER_BUCKET = true;
// Feature flag: hide the "Refund to Others" rows (To-Refund + Refunded) on both
// org-level and per-PF accordions. Paid-by-Others row is NOT affected.
const HIDE_REFUND_OTHERS = true;

const formatCurrency = (amount) => {
  if (amount == null) return '\u2014';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  try { return format(new Date(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
};

// Format-aware download for the Funding File / AP File columns. Generates the
// file on-the-fly using the org's configured format (Settings > Payment File
// Configuration), replacing the old pre-generated S3 links.
const FormatFileDownload = ({ pfId, kind, label, testid, batchName }) => {
  const [busy, setBusy] = useState(false);
  const endpoint = kind === 'funding' ? 'export-funding-file' : 'export-ap-file';
  const download = async () => {
    let acctId;
    try { acctId = JSON.parse(localStorage.getItem('current_account') || '{}').id; } catch { /* noop */ }
    setBusy(true);
    try {
      const res = await api.get(`/payments/v2/files/${pfId}/${endpoint}`, {
        params: { account_id: acctId }, responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchName || pfId}_${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${label} file downloaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to download ${label} file`);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={download}
          disabled={busy}
          className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 hover:underline disabled:opacity-50"
          data-testid={testid}
        >
          {busy ? <RefreshCw className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Download className="h-3.5 w-3.5 shrink-0" />}
          {label}
        </button>
      </TooltipTrigger>
      <TooltipContent className="text-xs max-w-[300px]">
        Download in the org&apos;s configured {kind === 'funding' ? 'Funding' : 'AP'} File Format (Settings → Payment File Configuration)
      </TooltipContent>
    </Tooltip>
  );
};

const STATUS_CONFIG = {
  pending_approval: { label: 'Pending Approval', color: 'bg-orange-500/15 text-orange-600 border-orange-500/20', icon: Clock },
  review: { label: 'In Review', color: 'bg-amber-500/15 text-amber-600 border-amber-500/20', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-500/15 text-blue-600 border-blue-500/20', icon: CheckCircle2 },
  paid: { label: 'Paid', color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20', icon: DollarSign },
};

const TABS = [
  { id: 'review', label: 'In Review' },
  { id: 'approved', label: 'Approved' },
  { id: 'paid', label: 'Paid' },
];

// ═══════════════════════════════════════════════════════════
// STATUS BADGE
// ═══════════════════════════════════════════════════════════
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.review;
  return (
    <Badge variant="outline" className={`${cfg.color} font-medium text-xs`} data-testid={`status-badge-${status}`}>
      {cfg.label}
    </Badge>
  );
};

// ═══════════════════════════════════════════════════════════
// PAYMENT FILE TABLE ROW
// ═══════════════════════════════════════════════════════════
const formatSyncTime = (dateStr) => {
  if (!dateStr) return null;
  try { return format(new Date(dateStr), 'MMM d, h:mm a'); } catch { return dateStr; }
};

const PFRow = ({ file, onView, onDownloadCsv, activeTab, stats, pmtStat, isAdmin, isPfAdmin, onResync, onResyncOverwrite, resyncingId, partnerName, onApprovePending, onAddBill, approvingId }) => {
  const s = stats || {};
  const ps = pmtStat || {};
  const hasIssues = (s.total_issues || 0) > 0 && !(s.all_resolved);
  const canResync = (isAdmin || isPfAdmin);
  return (
  <TableRow className="hover:bg-muted/50 transition-colors" data-testid={`pf-row-${file.payment_file_id}`}>
    <TableCell className="font-medium text-sm">
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          {file.batch_name || `PF-${file.batch_id}`}
          {activeTab === 'review' && hasIssues && (
            <>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-[10px] px-1.5 py-0" data-testid={`pf-issue-badge-${file.payment_file_id}`}>
                    <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />{s.total_issues}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">
                  {s.duplicates > 0 && <p>{s.duplicates} duplicate(s)</p>}
                  {s.mismatches > 0 && <p>{s.mismatches} mismatch(es)</p>}
                  {s.dismissed > 0 && <p>{s.dismissed} dismissed</p>}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20 text-[10px] px-1.5 py-0" data-testid={`pf-hidden-${file.payment_file_id}`}>
                    <EyeOff className="h-2.5 w-2.5 mr-0.5" />Hidden
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="text-xs">Resolve all issues to publish to Org users</TooltipContent>
              </Tooltip>
            </>
          )}
          {activeTab === 'review' && !hasIssues && s.invoice_count > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[10px] px-1.5 py-0" data-testid={`pf-published-${file.payment_file_id}`}>
                  <Send className="h-2.5 w-2.5 mr-0.5" />Published
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Visible to Org users (no unresolved issues)</TooltipContent>
            </Tooltip>
          )}
          {(activeTab === 'approved' || activeTab === 'paid') && ps.total_bills > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1.5 py-0 ${ps.paid_bills === ps.total_bills ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' : ps.paid_bills > 0 ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : 'bg-slate-500/10 text-slate-500 border-slate-500/20'}`}
                  data-testid={`pf-paid-stat-${file.payment_file_id}`}
                >
                  <DollarSign className="h-2.5 w-2.5 mr-0.5" />
                  {ps.paid_bills}/{ps.total_bills} Paid
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">
                <p>{ps.paid_bills} of {ps.total_bills} bills paid ({ps.pct_paid}%)</p>
                <p>Total Paid: {formatCurrency(ps.total_paid)}</p>
                {ps.paid_by_split && (
                  (ps.paid_by_split.billportal > 0 || ps.paid_by_split.anchor > 0 ||
                   ps.paid_by_split.customer > 0 || ps.paid_by_split.others > 0) && (
                    <div className="mt-1 pt-1 border-t border-border/40 space-y-0.5" data-testid={`pf-paid-by-split-${file.payment_file_id}`}>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Paid By</p>
                      {ps.paid_by_split.billportal > 0 && (
                        <p className="text-blue-600">BillPortal: {formatCurrency(ps.paid_by_split.billportal)} ({ps.paid_by_split.billportal_count})</p>
                      )}
                      {ps.paid_by_split.anchor > 0 && (
                        <p className="text-purple-600">{partnerName || 'Anchor'}: {formatCurrency(ps.paid_by_split.anchor)} ({ps.paid_by_split.anchor_count})</p>
                      )}
                      {!HIDE_CUSTOMER_BUCKET && ps.paid_by_split.customer > 0 && (
                        <p className="text-emerald-600">Customer: {formatCurrency(ps.paid_by_split.customer)} ({ps.paid_by_split.customer_count})</p>
                      )}
                      {ps.paid_by_split.others > 0 && (
                        <p className="text-slate-500">Others: {formatCurrency(ps.paid_by_split.others)} ({ps.paid_by_split.others_count})</p>
                      )}
                    </div>
                  )
                )}
                {(ps.total_to_refund > 0 || ps.total_refunded > 0) && (
                  <>
                    {ps.total_to_refund > 0 && <p className="text-amber-500">To Refund: {formatCurrency(ps.total_to_refund)}</p>}
                    {ps.total_refunded > 0 && <p className="text-emerald-600">Refunded: {formatCurrency(ps.total_refunded)}</p>}
                  </>
                )}
                {ps.total_svc_fee > 0 && <p>Service Fees: {formatCurrency(ps.total_svc_fee)}</p>}
                {ps.missing_txn_ref > 0 && <p className="text-amber-500">{ps.missing_txn_ref} missing txn ref</p>}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <span className="text-[10px] italic text-muted-foreground/70" data-testid={`pf-generated-${file.payment_file_id}`}>
          Generated {formatDate(file.generated_at)}
        </span>
      </div>
    </TableCell>
    <TableCell className="text-sm font-mono" data-testid={`pf-approved-amount-${file.payment_file_id}`}>{formatCurrency(((activeTab === 'approved' || activeTab === 'paid') && file.total_approved_amount != null) ? file.total_approved_amount : file.total_requested_amount)}</TableCell>
    {(activeTab === 'paid' || activeTab === 'approved') && (
      <TableCell className="text-sm font-mono" data-testid={`pf-total-paid-${file.payment_file_id}`}>
        {formatCurrency(ps.total_paid || 0)}
      </TableCell>
    )}
    {(activeTab === 'paid' || activeTab === 'approved') && (
      <TableCell className="text-sm font-mono text-indigo-600" data-testid={`pf-funds-pulled-${file.payment_file_id}`}>
        {formatCurrency(ps.funds_pulled || 0)}
      </TableCell>
    )}
    {activeTab === 'review' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.generated_at)}</TableCell>
    )}
    {activeTab === 'approved' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.approved_at)}</TableCell>
    )}
    {activeTab === 'paid' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.approved_at)}</TableCell>
    )}
    {activeTab === 'paid' && (
      <TableCell className="text-sm text-muted-foreground">{formatDate(file.paid_at)}</TableCell>
    )}
    {/* Task 4.1 — Funding file available in every status (Review/Approved/Paid) */}
    <TableCell className="text-sm" data-testid={`pf-funding-file-cell-${file.payment_file_id}`}>
      <FormatFileDownload pfId={file.payment_file_id} kind="funding" label="Funding" testid={`pf-funding-file-${file.payment_file_id}`} batchName={file.batch_name} />
    </TableCell>
    {(activeTab === 'approved' || activeTab === 'paid') && (
      <TableCell className="text-sm" data-testid={`pf-ap-file-cell-${file.payment_file_id}`}>
        <FormatFileDownload pfId={file.payment_file_id} kind="ap" label="AP File" testid={`pf-ap-file-${file.payment_file_id}`} batchName={file.batch_name} />
      </TableCell>
    )}
    <TableCell className="text-sm">
      <StatusBadge status={file.status} />
    </TableCell>
    <TableCell>
      <div className="flex flex-col items-start gap-0.5">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-3 text-xs"
            onClick={() => onView(file)}
            data-testid={`pf-view-${file.payment_file_id}`}
          >
            <Eye className="h-3.5 w-3.5 mr-1" /> View
          </Button>
          {activeTab === 'pending_approval' && false && (
            <>
              <Button
                variant="default"
                size="sm"
                className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700"
                onClick={() => onApprovePending(file)}
                disabled={approvingId === file.payment_file_id}
                data-testid={`pf-approve-${file.payment_file_id}`}
              >
                {approvingId === file.payment_file_id ? (
                  <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                )}
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={() => onAddBill(file)}
                data-testid={`pf-add-bill-${file.payment_file_id}`}
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add bill
              </Button>
            </>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onDownloadCsv(file)}
                data-testid={`pf-csv-${file.payment_file_id}`}
              >
                <FileDown className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="text-xs">Download as CSV</TooltipContent>
          </Tooltip>
          {canResync && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  data-testid={`pf-actions-menu-${file.payment_file_id}`}
                  disabled={resyncingId === file.payment_file_id}
                >
                  {resyncingId === file.payment_file_id ? (
                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <MoreVertical className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => onResync(file)}
                  disabled={resyncingId === file.payment_file_id}
                  data-testid={`pf-resync-${file.payment_file_id}`}
                  className="text-xs"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-2" />
                  Re-sync
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onResyncOverwrite(file)}
                  disabled={resyncingId === file.payment_file_id}
                  data-testid={`pf-resync-overwrite-${file.payment_file_id}`}
                  className="text-xs text-destructive focus:text-destructive"
                >
                  <AlertOctagon className="h-3.5 w-3.5 mr-2" />
                  Sync & Overwrite
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {file.last_synced_at && (
          <span className="text-[10px] text-muted-foreground pl-1" data-testid={`pf-last-synced-${file.payment_file_id}`}>
            Synced: {formatSyncTime(file.last_synced_at)}
          </span>
        )}
      </div>
    </TableCell>
  </TableRow>
  );
};

// ═══════════════════════════════════════════════════════════
// PAYMENT FILES LIST TABLE
// ═══════════════════════════════════════════════════════════
const PFTable = ({ files, loading, activeTab, onView, onDownloadCsv, page, totalPages, onPageChange, issueStats, pmtStats, isAdmin, isPfAdmin, onResync, onResyncOverwrite, resyncingId, partnerName, onApprovePending, onAddBill, approvingId }) => {
  if (loading) {
    return (
      <div className="space-y-3 p-4" data-testid="pf-table-loading">
        {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (!files.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="pf-table-empty">
        <FileText className="h-12 w-12 mb-3 opacity-40" />
        <p className="text-sm">No payment files in this status</p>
      </div>
    );
  }

  return (
    <div>
      <Table data-testid="pf-table">
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs font-semibold uppercase tracking-wider">Batch Name</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider">{(activeTab === 'approved' || activeTab === 'paid') ? 'Approved Amount' : 'Requested Amt'}</TableHead>
            {(activeTab === 'paid' || activeTab === 'approved') && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Paid</TableHead>
            )}
            {(activeTab === 'paid' || activeTab === 'approved') && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Funds Pulled</TableHead>
            )}
            {activeTab === 'review' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Generated</TableHead>
            )}
            {activeTab === 'approved' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Approved</TableHead>
            )}
            {activeTab === 'paid' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Approved</TableHead>
            )}
            {activeTab === 'paid' && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">Paid Date</TableHead>
            )}
            {/* Task 4.1 — Funding column shown for all statuses */}
            <TableHead className="text-xs font-semibold uppercase tracking-wider">Funding File</TableHead>
            {(activeTab === 'approved' || activeTab === 'paid') && (
              <TableHead className="text-xs font-semibold uppercase tracking-wider">AP File</TableHead>
            )}
            <TableHead className="text-xs font-semibold uppercase tracking-wider">Status</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider w-[180px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((f) => (
            <PFRow key={f.payment_file_id} file={f} onView={onView} onDownloadCsv={onDownloadCsv} activeTab={activeTab} stats={issueStats[f.payment_file_id]} pmtStat={pmtStats[f.payment_file_id]} isAdmin={isAdmin} isPfAdmin={isPfAdmin} onResync={onResync} onResyncOverwrite={onResyncOverwrite} resyncingId={resyncingId} partnerName={partnerName} onApprovePending={onApprovePending} onAddBill={onAddBill} approvingId={approvingId} />
          ))}
        </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-3" data-testid="pf-pagination">
          <span className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════
export default function PaymentsV2Page() {
  const { user } = getAuth();
  const navigate = useNavigate();

  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });

  useEffect(() => {
    const check = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) setCurrentAccount(acc);
    };
    check();
    const iv = setInterval(check, 1000);
    const to = setTimeout(() => clearInterval(iv), 10000);
    return () => { clearInterval(iv); clearTimeout(to); };
  }, [currentAccount?.id]);

  const [activeTab, setActiveTab] = useState('review');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({ review: 0, approved: 0, paid: 0 });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [issueStats, setIssueStats] = useState({});
  const [pmtStats, setPmtStats] = useState({});
  const [resyncingId, setResyncingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const handleApprovePending = useCallback(async (file) => {
    setApprovingId(file.payment_file_id);
    try {
      const { data } = await api.post(
        `/payment-schedule/pending/${file.payment_file_id}/approve`, {},
        { params: { account_id: currentAccount.id } },
      );
      toast.success(`Payment file generated — ${data.total_bills} bill(s)` + (data.added_bills ? `, ${data.added_bills} added` : ''));
      fetchFiles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve payment file');
    } finally {
      setApprovingId(null);
    }
  }, [currentAccount?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddBill = useCallback(() => {
    navigate('/paas/upload');
  }, [navigate]);
  const [paymentStats, setPaymentStats] = useState(null);
  const [overwriteConfirmFile, setOverwriteConfirmFile] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [fundsPulledOpen, setFundsPulledOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkParsedRows, setBulkParsedRows] = useState([]);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkCommitting, setBulkCommitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [statsBreakdownOpen, setStatsBreakdownOpen] = useState(false);
  const limit = 20;
  const isAdmin = isSuperAdmin();
  const isPfAdmin = isSuperPfAdmin();

  // Detail view state
  const [selectedPF, setSelectedPF] = useState(null);

  const fetchFiles = useCallback(async () => {
    if (!currentAccount?.id) return;
    setLoading(true);
    try {
      // STEP 1: Fetch the PF list (fast, always first so we know IDs)
      const { data } = await api.get('/payments/v2/files', {
        params: { account_id: currentAccount.id, status: activeTab, page, limit },
      });
      const fetchedFiles = data.files || [];
      setTotal(data.total || 0);
      if (data.status_counts) setStatusCounts(data.status_counts);

      if (fetchedFiles.length === 0) {
        setFiles([]);
        setIssueStats({});
        setPmtStats({});
        return;
      }

      const pfIds = fetchedFiles.map(f => f.payment_file_id).join(',');

      // STEP 2: Parallelize issue-stats + payment-stats for approved/paid
      const needPmtStats = (activeTab === 'approved' || activeTab === 'paid');
      const [issueRes, pmtRes] = await Promise.all([
        api.get('/payments/v2/files/batch-issue-stats', {
          params: { account_id: currentAccount.id, pf_ids: pfIds },
        }).catch(() => ({ data: { stats: {} } })),
        needPmtStats
          ? api.get('/payments/v2/files/batch-payment-stats', {
              params: { account_id: currentAccount.id, pf_ids: pfIds },
            }).catch(() => ({ data: { stats: {} } }))
          : Promise.resolve({ data: { stats: {} } }),
      ]);

      const issueStatsData = issueRes.data.stats || {};
      setIssueStats(issueStatsData);
      setPmtStats(needPmtStats ? (pmtRes.data.stats || {}) : {});

      // RBAC: For "In Review" tab, non-super-admins only see PFs with all issues resolved
      if (activeTab === 'review' && !isAdmin) {
        const visible = fetchedFiles.filter(f => {
          const s = issueStatsData[f.payment_file_id];
          return !s || s.total_issues === 0 || s.all_resolved;
        });
        setFiles(visible);
        setTotal(visible.length);
      } else {
        setFiles(fetchedFiles);
      }
    } catch (err) {
      console.error('Failed to fetch payment files:', err);
      toast.error('Failed to load payment files');
    } finally {
      setLoading(false);
    }
  }, [currentAccount?.id, activeTab, page, isAdmin]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  // Fetch aggregate stats
  const fetchPaymentStats = useCallback(async () => {
    if (!currentAccount?.id) return;
    try {
      const { data } = await api.get('/payments/v2/stats', { params: { account_id: currentAccount.id } });
      setPaymentStats(data);
    } catch { /* silent */ }
  }, [currentAccount?.id]);

  useEffect(() => { fetchPaymentStats(); }, [fetchPaymentStats]);

  // Reset page when tab changes
  useEffect(() => { setPage(1); }, [activeTab]);

  const totalPages = Math.ceil(total / limit);

  const handleView = (file) => setSelectedPF(file);

  const handleBack = () => {
    setSelectedPF(null);
    fetchFiles();
    fetchPaymentStats();
  };

  const handleResync = async (file) => {
    setResyncingId(file.payment_file_id);
    try {
      const { data } = await api.post(`/payments/v2/files/${file.payment_file_id}/resync`, { force_overwrite: false }, {
        params: { account_id: currentAccount.id },
      });
      const totals = data?.sync_result?.totals || {};
      toast.success(`Re-sync completed for ${file.batch_name || file.payment_file_id} — ${totals.total_updated || 0} records updated`);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-sync failed');
    } finally {
      setResyncingId(null);
    }
  };

  const handleResyncOverwrite = (file) => {
    setOverwriteConfirmFile(file);
  };

  const confirmResyncOverwrite = async () => {
    if (!overwriteConfirmFile) return;
    const file = overwriteConfirmFile;
    setOverwriteConfirmFile(null);
    setResyncingId(file.payment_file_id);
    try {
      const { data } = await api.post(`/payments/v2/files/${file.payment_file_id}/resync`, { force_overwrite: true }, {
        params: { account_id: currentAccount.id },
      });
      const totals = data?.sync_result?.totals || {};
      toast.success(`Sync & Overwrite completed for ${file.batch_name || file.payment_file_id} — all data refreshed from source`);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync & Overwrite failed');
    } finally {
      setResyncingId(null);
    }
  };

  const handleActionComplete = () => {
    setSelectedPF(null);
    fetchFiles();
    fetchPaymentStats();
  };

  const handleDownloadCsv = async (file) => {
    try {
      const response = await api.get(`/payments/v2/files/${file.payment_file_id}/export-csv`, {
        params: { account_id: currentAccount.id },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${file.batch_name || file.payment_file_id}_utilities.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download CSV');
    }
  };

  const handleDownloadReconcileCsv = async () => {
    if (!currentAccount?.id) return;
    try {
      const response = await api.get('/payments/v2/unaccounted-bills/export', {
        params: { account_id: currentAccount.id },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `to_reconcile_${currentAccount.id.slice(0, 8)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download To-Reconcile CSV');
    }
  };

  // ─── BULK UPLOAD HANDLERS ───────────────────────────────
  const parseBulkCsv = async (file) => {
    const text = await file.text();
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim().toLowerCase());
    const billIdIdx = headers.findIndex(h => h.includes('bill id') || h === 'bill_id');
    const batchIdIdx = headers.findIndex(h => h.includes('batch id') || h === 'batch_id');
    const pmtDateIdx = headers.findIndex(h => h.includes('payment date') || h === 'payment_date');
    const amountIdx = headers.findIndex(h => h.includes('amount paid') || h === 'amount_paid');
    const feeIdx = headers.findIndex(h => h.includes('service fee') || h === 'service_fee');
    const refIdx = headers.findIndex(h => h.includes('transaction ref') || h === 'transaction_ref');
    const typeIdx = headers.findIndex(h => h.includes('transaction type') || h === 'transaction_type');
    const paidByIdx = headers.findIndex(h => h.includes('paid by') || h === 'paid_by');

    const cleanNum = (v) => {
      if (!v) return 0;
      return parseFloat(String(v).replace(/[$,"\s]/g, '')) || 0;
    };

    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      // Handle CSV with quoted fields
      const parts = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
      if (!parts) continue;
      const vals = parts.map(p => p.replace(/^"|"$/g, '').trim());
      const billId = billIdIdx >= 0 ? vals[billIdIdx] : '';
      if (!billId || billId.length < 10) continue; // skip non-UUID rows

      rows.push({
        bill_id: billId,
        batch_id: batchIdIdx >= 0 ? (vals[batchIdIdx] || '') : '',
        payment_date: pmtDateIdx >= 0 ? (vals[pmtDateIdx] || '') : '',
        amount_paid: cleanNum(amountIdx >= 0 ? vals[amountIdx] : 0),
        service_fee: cleanNum(feeIdx >= 0 ? vals[feeIdx] : 0),
        transaction_ref: refIdx >= 0 ? (vals[refIdx] || '') : '',
        transaction_type: typeIdx >= 0 ? (vals[typeIdx] || '') : '',
        paid_by_name: paidByIdx >= 0 ? (vals[paidByIdx] || 'Manoj') : 'Manoj',
      });
    }
    return rows;
  };

  const handleBulkFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkFile(file);
    setBulkPreview(null);
    setBulkResult(null);

    try {
      const rows = await parseBulkCsv(file);
      setBulkParsedRows(rows);
      if (rows.length === 0) {
        toast.error('No valid rows found in CSV');
        return;
      }
      // Send preview request
      setBulkLoading(true);
      const { data } = await api.post('/payments/v2/bulk-upload-transactions/preview', {
        rows,
        account_id: null, // all orgs
        paid_by_name: 'Manoj',
      });
      setBulkPreview(data);
    } catch (err) {
      toast.error('Failed to parse/preview CSV');
      console.error(err);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkCommit = async () => {
    if (!bulkParsedRows.length) return;
    setBulkCommitting(true);
    try {
      const { data } = await api.post('/payments/v2/bulk-upload-transactions/commit', {
        rows: bulkParsedRows,
        account_id: null,
        paid_by_name: 'Manoj',
        paid_by: user?.id || '',
      });
      setBulkResult(data);
      toast.success(`Bulk upload complete: ${data.success} transactions recorded`);
      fetchFiles();
      fetchPaymentStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkCommitting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/payments/v2/bulk-upload-transactions/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'payment_transactions_template.csv';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  // ─── DETAIL VIEW ─────────────────────────────────────────
  if (selectedPF) {
    return (
      <PaymentFileDetail
        pfId={selectedPF.payment_file_id}
        accountId={currentAccount?.id}
        userId={user?.id}
        userName={user?.name}
        userRole={currentAccount?.role || (isSuperAdmin() ? 'admin' : 'viewer')}
        onBack={handleBack}
        onActionComplete={handleActionComplete}
      />
    );
  }

  // ─── LIST VIEW ───────────────────────────────────────────
  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
      <div className="space-y-6" data-testid="payments-v2-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-foreground" strokeWidth={2} />
            <h1 className="text-2xl font-bold tracking-tight">Payments</h1>
            <NotesIcon pageKey="payments_v2" pageLabel="Payments" accountId={currentAccount?.id} />
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs gap-1.5"
                onClick={() => { setBulkUploadOpen(true); setBulkFile(null); setBulkParsedRows([]); setBulkPreview(null); setBulkResult(null); }}
                data-testid="bulk-upload-transactions-btn"
              >
                <Upload className="h-3.5 w-3.5" />
                Upload Transactions
              </Button>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4" data-testid="payments-summary-cards">
          {TABS.map(({ id, label }) => {
            const cfg = STATUS_CONFIG[id];
            const Icon = cfg.icon;
            const count = statusCounts[id] || 0;
            return (
              <button
                key={id}
                onClick={() => { setActiveTab(id); setSelectedPF(null); }}
                className={`flex items-center gap-4 p-4 border rounded-lg transition-all ${
                  activeTab === id
                    ? 'border-foreground/20 bg-muted/50 ring-1 ring-foreground/10'
                    : 'border-border hover:border-foreground/10 hover:bg-muted/30'
                }`}
                data-testid={`payments-card-${id}`}
              >
                <div className={`flex items-center justify-center h-10 w-10 rounded-md ${cfg.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="text-left">
                  <p className="text-2xl font-bold">{count}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Aggregate Stats Bar */}
        {paymentStats && (paymentStats.review_count > 0 || paymentStats.approved_count > 0 || paymentStats.paid_count > 0) && (
          <div className="p-4 bg-muted/30 border border-border rounded-lg space-y-3" data-testid="payments-aggregate-stats">
          <div className="grid grid-cols-4 gap-4">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-blue-500/15 text-blue-600">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Total Funds Requested <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <p className="text-lg font-bold">{formatCurrency(paymentStats.total_funds_requested)}</p>
                      {(paymentStats.total_funds_approved || 0) > 0 && Math.abs((paymentStats.total_funds_approved || 0) - (paymentStats.total_funds_requested || 0)) > 0.01 && (
                        <p className="text-[10px] text-blue-700 font-medium" data-testid="kpi-total-approved-sub">
                          Approved: {formatCurrency(paymentStats.total_funds_approved)}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[280px] text-xs p-3">
                  <p>Sum of the <strong>Pay</strong> amount across all Payment Files (In&nbsp;Review + Approved + Paid).</p>
                  <p className="mt-1"><strong>Approved</strong> is the manager-approved amount — shown only when it differs from Requested.</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setFundsPulledOpen(true)}
                    className="flex items-center gap-3 cursor-pointer text-left rounded-md -m-1 p-1 hover:bg-muted/50 focus:bg-muted/50 focus:outline-none transition-colors"
                    data-testid="kpi-funds-pulled"
                  >
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-violet-500/15 text-violet-600">
                      <DollarSign className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Funds Pulled <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <p className="text-lg font-bold underline decoration-dotted decoration-muted-foreground/40 underline-offset-2" data-testid="kpi-funds-pulled-value">{formatCurrency(paymentStats.total_funds_pulled)}</p>
                      {paymentStats.total_funds_pulled_bank > 0 && (
                        <p className="text-[10px] text-emerald-600 font-medium" data-testid="kpi-funds-pulled-bank-sub">
                          inc. {formatCurrency(paymentStats.total_funds_pulled_bank)} from bank statements
                        </p>
                      )}
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[340px] text-xs p-3">
                  <p className="font-semibold mb-1">Funds Pulled (Net)</p>
                  <p className="mb-1">Net actual fund pulls from <strong>uploaded bank statements</strong> (via Upload Credit File) + any legacy fund-pull ledger entries.</p>
                  <ul className="text-[10px] mt-1 space-y-0.5 text-muted-foreground">
                    <li>Gross fund-pull credits: {formatCurrency(paymentStats.total_funds_pulled_bank_gross || paymentStats.total_funds_pulled_bank || 0)} ({paymentStats.total_funds_pulled_bank_count || 0} lines)</li>
                    {(paymentStats.total_funds_pulled_bank_reversals || 0) > 0 && (
                      <li className="text-amber-700">− R03 reversals: {formatCurrency(paymentStats.total_funds_pulled_bank_reversals)} (failed pulls returned)</li>
                    )}
                    <li className="font-medium text-foreground">= Bank net: {formatCurrency(paymentStats.total_funds_pulled_bank || 0)}</li>
                    <li>+ Legacy ledger: {formatCurrency(paymentStats.total_funds_pulled_legacy || 0)}</li>
                  </ul>
                  <p className="text-[10px] mt-2 text-muted-foreground italic">Excludes vendor refunds, R03 reversals & bank fees — see Credit/Debit Report for the full bank picture.</p>
                  <p className="text-[10px] mt-1 text-foreground font-medium">Click the value to see the full breakdown by date & download CSV.</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-emerald-500/15 text-emerald-600">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Total Paid <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <p className="text-lg font-bold">{formatCurrency(paymentStats.total_paid)}</p>
                      {((paymentStats.paid_by_bp || 0) > 0 || (paymentStats.paid_by_partner || paymentStats.paid_by_anchor || 0) > 0 || (paymentStats.paid_by_customer || 0) > 0 || (paymentStats.paid_by_others || 0) > 0 || (paymentStats.paid_unaccounted || 0) > 0.01) && (
                        <p className="text-[10px] text-muted-foreground font-medium" data-testid="paid-breakdown">
                          {(paymentStats.paid_by_bp || 0) > 0 && <>BP: {formatCurrency(paymentStats.paid_by_bp)}</>}
                          {(paymentStats.paid_by_partner || paymentStats.paid_by_anchor || 0) > 0 && <> | {paymentStats.partner_name || 'Anchor'}: {formatCurrency(paymentStats.paid_by_partner || paymentStats.paid_by_anchor)}</>}
                          {!HIDE_CUSTOMER_BUCKET && (paymentStats.paid_by_customer || 0) > 0 && <> | Customer: {formatCurrency(paymentStats.paid_by_customer)}</>}
                          {(paymentStats.paid_by_others || 0) > 0 && <> | Others: {formatCurrency(paymentStats.paid_by_others)}</>}
                          {(paymentStats.paid_unaccounted || 0) > 0.01 && (
                            <> | <span className="text-amber-600 font-semibold" title="Bills marked paid but with no matching bank transaction record yet. Reconcile in Variance Report.">To Reconcile: {formatCurrency(paymentStats.paid_unaccounted)}</span></>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[340px] text-xs p-3">
                  <p className="font-semibold mb-1">Total Paid — {formatCurrency(paymentStats.total_paid)}</p>
                  <p className="mb-1">Sum of paid amounts across all Payment Files.</p>
                  <p className="text-muted-foreground text-[10px] mb-2 italic">
                    For the most up-to-date payment activity, open the <strong className="not-italic text-foreground">Credit/Debit Report → Bank Transactions</strong> tab — it gives the clearest view of what's actually moved through the bank, including fund pulls.
                  </p>
                  <p className="text-muted-foreground mt-1">
                    <strong>BP</strong>: payments made by BillPortal.<br/>
                    <strong>Others</strong>: payments made by 3rd parties.<br/>
                    <strong className="text-amber-600">To Reconcile</strong>: bills marked paid that don't yet have a matching bank transaction — reconcile in the Variance Report.
                  </p>
                  <p className="mt-1 text-[10px]">BP + Others + To Reconcile = Total Paid</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-3 cursor-help" data-testid="refund-kpi-tile">
                    <div className="flex items-center justify-center h-9 w-9 rounded-md bg-red-500/15 text-red-600">
                      <ArrowLeft className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">Refunds <Info className="h-3 w-3 text-muted-foreground/60" /></p>
                      <div className="flex items-baseline gap-2.5">
                        <span className="text-sm font-bold text-amber-600" data-testid="kpi-to-refund">{formatCurrency(paymentStats.total_to_refund || 0)}</span>
                        <span className="text-[10px] text-amber-600/70 uppercase">To Refund</span>
                        <span className="text-muted-foreground/40">/</span>
                        <span className="text-sm font-bold text-emerald-600" data-testid="kpi-refunded">{formatCurrency(paymentStats.total_refunded || 0)}</span>
                        <span className="text-[10px] text-emerald-600/70 uppercase">Refunded</span>
                      </div>
                      {((paymentStats.to_refund_anchor || 0) > 0 || (paymentStats.to_refund_customer || 0) > 0 || (paymentStats.to_refund_others || 0) > 0) && (
                        <p className="text-[10px] text-muted-foreground font-medium mt-0.5" data-testid="refund-breakdown">
                          {(paymentStats.to_refund_anchor || 0) > 0 && <>{paymentStats.partner_name || 'Anchor'}: {formatCurrency(paymentStats.to_refund_anchor)}</>}
                          {!HIDE_CUSTOMER_BUCKET && (paymentStats.to_refund_customer || 0) > 0 && <>{(paymentStats.to_refund_anchor || 0) > 0 ? ' | ' : ''}Customer: {formatCurrency(paymentStats.to_refund_customer)}</>}
                          {!HIDE_REFUND_OTHERS && (paymentStats.to_refund_others || 0) > 0 && <> | Others: {formatCurrency(paymentStats.to_refund_others)}</>}
                        </p>
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[340px] text-xs p-3">
                  <p className="font-semibold mb-1">Refund Split (org-wide, netted)</p>
                  <p className="mb-1"><strong>To Refund</strong>: outstanding refund obligation per recipient bucket, after netting bulk Process Refund events against open bills.</p>
                  <p className="mb-1"><strong>Refunded</strong>: per-bill completions <em>plus</em> bulk Process Refund events.</p>
                  <p className="text-[10px] text-muted-foreground italic mt-1">Open <strong className="not-italic text-foreground">Refunds Report</strong> for the full per-bill + per-event view, with filters by bucket and status.</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Breakdown toggle */}
            <button
              onClick={() => setStatsBreakdownOpen(prev => !prev)}
              className="text-[11px] text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 transition-colors"
              data-testid="stats-breakdown-toggle"
            >
              {statsBreakdownOpen ? <ChevronLeft className="h-3 w-3 rotate-[-90deg]" /> : <ChevronRight className="h-3 w-3 rotate-90" />}
              {statsBreakdownOpen ? 'Hide' : 'Show'} breakdown by status
            </button>

            {statsBreakdownOpen && (
              <div className="border-t border-border pt-3 grid grid-cols-4 gap-4 text-xs animate-in fade-in slide-in-from-top-1 duration-200" data-testid="stats-breakdown-panel">
                {/* Requested / Approved breakdown — aligns under "Total Funds Requested" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Requested in</p>
                  {paymentStats.review_requested > 0 && (
                    <div className="flex justify-between" data-testid="breakdown-review-requested"><span className="text-amber-600">Review Tab</span><span className="font-mono">{formatCurrency(paymentStats.review_requested)}</span></div>
                  )}
                  {(paymentStats.approved_requested || 0) > 0 && (
                    <div className="flex justify-between" data-testid="breakdown-approved-requested"><span className="text-blue-600">Approved Tab</span><span className="font-mono">{formatCurrency(paymentStats.approved_requested)}</span></div>
                  )}
                  <div className="flex justify-between" data-testid="breakdown-paid-requested"><span className="text-emerald-600">Paid Tab</span><span className="font-mono">{formatCurrency(paymentStats.paid_requested)}</span></div>
                  {/* Approved amount sub-rows — shown only when ApprovedAmount differs from PaymentAmount */}
                  {((paymentStats.review_approved || 0) + (paymentStats.approved_approved || 0) + (paymentStats.paid_approved || 0) > 0)
                    && Math.abs(
                      ((paymentStats.review_approved || 0) + (paymentStats.approved_approved || 0) + (paymentStats.paid_approved || 0))
                      - ((paymentStats.review_requested || 0) + (paymentStats.approved_requested || 0) + (paymentStats.paid_requested || 0))
                    ) > 0.01 && (
                    <div className="border-t border-border/40 pt-1.5 mt-1.5 space-y-1" data-testid="breakdown-approved-section">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">Approved (post-approval)</p>
                      {(paymentStats.review_approved || 0) > 0 && (
                        <div className="flex justify-between pl-3" data-testid="breakdown-review-approved"><span className="text-amber-600 text-xs">↳ Review Tab</span><span className="font-mono text-xs">{formatCurrency(paymentStats.review_approved)}</span></div>
                      )}
                      {(paymentStats.approved_approved || 0) > 0 && (
                        <div className="flex justify-between pl-3" data-testid="breakdown-approved-approved"><span className="text-blue-600 text-xs">↳ Approved Tab</span><span className="font-mono text-xs">{formatCurrency(paymentStats.approved_approved)}</span></div>
                      )}
                      {(paymentStats.paid_approved || 0) > 0 && (
                        <div className="flex justify-between pl-3" data-testid="breakdown-paid-approved"><span className="text-emerald-600 text-xs">↳ Paid Tab</span><span className="font-mono text-xs">{formatCurrency(paymentStats.paid_approved)}</span></div>
                      )}
                    </div>
                  )}
                </div>
                {/* Funds Pulled breakdown — aligns under "Funds Pulled" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Funds Pulled</p>
                  {(paymentStats.approved_pulled || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-blue-600">From Approved PFs</span><span className="font-mono">{formatCurrency(paymentStats.approved_pulled)}</span></div>
                  )}
                  {(paymentStats.paid_pulled || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-emerald-600">From Paid PFs</span><span className="font-mono">{formatCurrency(paymentStats.paid_pulled)}</span></div>
                  )}
                  {paymentStats.latest_funds_pull_amount > 0 && (
                    <div className="flex justify-between items-end pt-1" data-testid="breakdown-latest-pull">
                      <span className="text-violet-600 leading-tight">
                        Latest pull
                        {paymentStats.latest_funds_pull_date && (
                          <span className="block text-[9px] text-muted-foreground font-normal">{formatDate(paymentStats.latest_funds_pull_date)}</span>
                        )}
                      </span>
                      <span className="font-mono">{formatCurrency(paymentStats.latest_funds_pull_amount)}</span>
                    </div>
                  )}
                </div>
                {/* Payments breakdown — aligns under "Total Paid" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Payments</p>
                  <div className="flex justify-between"><span className="text-emerald-600">Paid by BP</span><span className="font-mono">{formatCurrency(paymentStats.paid_by_bp || 0)}</span></div>
                  {(paymentStats.paid_by_partner || paymentStats.paid_by_anchor || 0) > 0 && (
                    <div className="flex justify-between" data-testid="paid-by-partner-row"><span className="text-purple-600">Paid by {paymentStats.partner_name || 'Anchor'}</span><span className="font-mono">{formatCurrency(paymentStats.paid_by_partner || paymentStats.paid_by_anchor)}</span></div>
                  )}
                  {!HIDE_CUSTOMER_BUCKET && (paymentStats.paid_by_customer || 0) > 0 && (
                    <div className="flex justify-between" data-testid="paid-by-customer-row"><span className="text-cyan-700">Paid by Customer</span><span className="font-mono">{formatCurrency(paymentStats.paid_by_customer)}</span></div>
                  )}
                  {(paymentStats.paid_by_others || 0) > 0 && (
                    <div className="flex justify-between"><span className="text-violet-600">Paid by Others</span><span className="font-mono">{formatCurrency(paymentStats.paid_by_others || 0)}</span></div>
                  )}
                  {(paymentStats.paid_unaccounted || 0) > 0.01 && (
                    <div className="flex justify-between items-center gap-2" title="Bills marked paid that don't yet have a matching bank transaction. Reconcile in the Variance Report.">
                      <span className="text-amber-600">To Reconcile</span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-amber-700">{formatCurrency(paymentStats.paid_unaccounted)}</span>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={handleDownloadReconcileCsv}
                            className="text-[10px] text-amber-700 hover:text-amber-900 underline bg-transparent border-0 p-0 cursor-pointer"
                            data-testid="download-reconcile-csv"
                            title="Download To-Reconcile bills as CSV (Super Admin only)"
                          >CSV</button>
                        )}
                      </div>
                    </div>
                  )}
                  {paymentStats.total_svc_fee > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">Svc Fees</span><span className="font-mono">{formatCurrency(paymentStats.total_svc_fee)}</span></div>
                  )}
                  {/* Cross-status paid breakdown — how much is paid from In-Review / Approved / Paid PFs.
                       All three already roll up into the main Total Paid above. */}
                  {((paymentStats.approved_paid || 0) > 0 || (paymentStats.review_paid || 0) > 0) && (
                    <div className="border-t border-border/40 pt-1.5 mt-1.5 space-y-1" data-testid="paid-by-pf-status">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">By PF Status</p>
                      {(paymentStats.review_paid || 0) > 0 && (
                        <div className="flex justify-between" data-testid="paid-in-review-row" title="Bills already paid that sit inside Payment Files still in Review. Already included in Total Paid above.">
                          <span className="text-slate-600">↳ In-Review Bills Paid</span>
                          <span className="font-mono">{formatCurrency(paymentStats.review_paid || 0)}</span>
                        </div>
                      )}
                      {(paymentStats.approved_paid || 0) > 0 && (
                        <div className="flex justify-between" data-testid="paid-approved-row" title="Bills already paid that sit inside Approved Payment Files. Already included in Total Paid above.">
                          <span className="text-blue-600">↳ Approved Bills Paid</span>
                          <span className="font-mono">{formatCurrency(paymentStats.approved_paid || 0)}</span>
                        </div>
                      )}
                      {(paymentStats.paid_paid || 0) > 0 && (
                        <div className="flex justify-between" data-testid="paid-final-row" title="Bills paid inside Payment Files marked Paid. Already included in Total Paid above.">
                          <span className="text-emerald-700">↳ Paid PFs</span>
                          <span className="font-mono">{formatCurrency(paymentStats.paid_paid || 0)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Refunds breakdown — aligns under "Refunds" */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Refunds</p>
                  <div className="flex justify-between"><span className="text-amber-600">To Refund</span><span className="font-mono">{formatCurrency(paymentStats.total_to_refund || 0)}</span></div>
                  {(paymentStats.to_refund_anchor || 0) > 0 && (
                    <div className="flex justify-between pl-3" data-testid="to-refund-partner-row">
                      <span className="text-purple-600 text-xs">↳ to {paymentStats.partner_name || 'Anchor'}</span>
                      <span className="font-mono text-xs">{formatCurrency(paymentStats.to_refund_anchor)}</span>
                    </div>
                  )}
                  {!HIDE_CUSTOMER_BUCKET && (paymentStats.to_refund_customer || 0) > 0 && (
                    <div className="flex justify-between pl-3" data-testid="to-refund-customer-row">
                      <span className="text-cyan-700 text-xs">↳ to Customer</span>
                      <span className="font-mono text-xs">{formatCurrency(paymentStats.to_refund_customer)}</span>
                    </div>
                  )}
                  {!HIDE_REFUND_OTHERS && (paymentStats.to_refund_others || 0) > 0 && (
                    <div className="flex justify-between pl-3">
                      <span className="text-violet-600 text-xs">↳ to Others</span>
                      <span className="font-mono text-xs">{formatCurrency(paymentStats.to_refund_others)}</span>
                    </div>
                  )}
                  <div className="flex justify-between"><span className="text-emerald-600">Refunded</span><span className="font-mono">{formatCurrency(paymentStats.total_refunded || 0)}</span></div>
                  {(paymentStats.refunded_anchor || 0) > 0 && (
                    <div className="flex justify-between pl-3"><span className="text-purple-600 text-xs">↳ to {paymentStats.partner_name || 'Anchor'}</span><span className="font-mono text-xs">{formatCurrency(paymentStats.refunded_anchor)}</span></div>
                  )}
                  {(paymentStats.refunded_customer || 0) > 0 && (
                    <div className="flex justify-between pl-3" data-testid="refunded-customer-row"><span className="text-cyan-700 text-xs">↳ to Customer</span><span className="font-mono text-xs">{formatCurrency(paymentStats.refunded_customer)}</span></div>
                  )}
                  {!HIDE_REFUND_OTHERS && (paymentStats.refunded_others || 0) > 0 && (
                    <div className="flex justify-between pl-3"><span className="text-violet-600 text-xs">↳ to Others</span><span className="font-mono text-xs">{formatCurrency(paymentStats.refunded_others)}</span></div>
                  )}
                  {/* Source split: per-bill completions vs bulk Process Refund events */}
                  {((paymentStats.refunded_bill_anchor || 0) + (paymentStats.refunded_bill_customer || 0) + (paymentStats.refunded_bill_others || 0) > 0
                    || (paymentStats.refunded_bulk_anchor || 0) + (paymentStats.refunded_bulk_customer || 0) + (paymentStats.refunded_bulk_others || 0) > 0) && (
                    <div className="border-t border-border/40 pt-1.5 mt-1 space-y-0.5" data-testid="refund-source-split">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/80">Source</p>
                      <div className="flex justify-between" data-testid="refunded-source-bill">
                        <span className="text-slate-600 text-xs">↳ Per-bill completions</span>
                        <span className="font-mono text-xs">{formatCurrency(
                          (paymentStats.refunded_bill_anchor || 0) + (paymentStats.refunded_bill_customer || 0) + (paymentStats.refunded_bill_others || 0)
                        )}</span>
                      </div>
                      <div className="flex justify-between" data-testid="refunded-source-bulk">
                        <span className="text-slate-600 text-xs">↳ Bulk (Process Refund)</span>
                        <span className="font-mono text-xs">{formatCurrency(
                          (paymentStats.refunded_bulk_anchor || 0) + (paymentStats.refunded_bulk_customer || 0) + (paymentStats.refunded_bulk_others || 0)
                        )}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-border" data-testid="payments-tabs">
          <div className="flex gap-0">
            {TABS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === id
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`payments-tab-${id}`}
              >
                {label}
                {(statusCounts[id] || 0) > 0 && (
                  <span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">
                    {statusCounts[id]}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="payments-table-container">
          <PFTable
            files={files}
            loading={loading}
            activeTab={activeTab}
            onView={handleView}
            onDownloadCsv={handleDownloadCsv}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            issueStats={issueStats}
            pmtStats={pmtStats}
            isAdmin={isAdmin}
            isPfAdmin={isPfAdmin}
            onResync={handleResync}
            onResyncOverwrite={handleResyncOverwrite}
            resyncingId={resyncingId}
            partnerName={paymentStats?.partner_name}
            onApprovePending={handleApprovePending}
            onAddBill={handleAddBill}
            approvingId={approvingId}
          />
        </div>


        {/* Funds Pulled drilldown modal */}
        <FundsPulledBreakdownModal
          open={fundsPulledOpen}
          onOpenChange={setFundsPulledOpen}
          accountId={currentAccount?.id}
          orgName={currentAccount?.name || currentAccount?.organisation_name || ''}
        />


        {/* Sync & Overwrite Confirmation Dialog */}
        <Dialog open={!!overwriteConfirmFile} onOpenChange={(open) => { if (!open) setOverwriteConfirmFile(null); }}>
          <DialogContent className="sm:max-w-md" data-testid="sync-overwrite-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertOctagon className="h-5 w-5" />
                Sync & Overwrite
              </DialogTitle>
              <DialogDescription className="pt-2 text-sm leading-relaxed">
                This will overwrite <strong>all manual changes</strong> for{' '}
                <strong>{overwriteConfirmFile?.batch_name || 'this payment file'}</strong>{' '}
                with the latest data from the source system.
                <br /><br />
                Any updates to payment amounts, notes, service charges, and other manually
                edited fields will be <strong className="text-destructive">permanently erased</strong>.
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setOverwriteConfirmFile(null)} data-testid="sync-overwrite-cancel">
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmResyncOverwrite} data-testid="sync-overwrite-confirm">
                Overwrite All Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Bulk Upload Transactions Dialog */}
        <Dialog open={bulkUploadOpen} onOpenChange={(open) => { if (!open) setBulkUploadOpen(false); }}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bulk-upload-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Upload Payment Transactions
              </DialogTitle>
              <DialogDescription className="pt-1 text-sm">
                Upload a CSV file with transaction details to record payments in bulk across all customers.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Template Download + File Upload */}
              <div className="flex items-center gap-3">
                <Button variant="outline" size="sm" className="text-xs gap-1.5" onClick={handleDownloadTemplate} data-testid="bulk-download-template">
                  <Download className="h-3.5 w-3.5" />
                  Download Template
                </Button>
                <div className="flex-1">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkFileChange}
                    className="block w-full text-xs text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-border file:text-xs file:font-medium file:bg-muted file:text-foreground hover:file:bg-muted/70 file:cursor-pointer cursor-pointer"
                    data-testid="bulk-file-input"
                  />
                </div>
              </div>

              {bulkFile && <p className="text-xs text-muted-foreground">File: {bulkFile.name} ({bulkParsedRows.length} rows parsed)</p>}

              {bulkLoading && (
                <div className="flex items-center gap-2 py-4 justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Validating transactions...</span>
                </div>
              )}

              {/* Preview Results */}
              {bulkPreview && !bulkResult && (
                <div className="space-y-3">
                  <div className="grid grid-cols-4 gap-3">
                    <div className="p-3 rounded-lg border bg-muted/30 text-center">
                      <p className="text-lg font-bold">{bulkPreview.total_rows}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Total Rows</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-emerald-50 text-center">
                      <p className="text-lg font-bold text-emerald-700">{bulkPreview.matched}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Matched</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-amber-50 text-center">
                      <p className="text-lg font-bold text-amber-700">{bulkPreview.already_paid}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Already Paid</p>
                    </div>
                    <div className="p-3 rounded-lg border bg-red-50 text-center">
                      <p className="text-lg font-bold text-red-700">{bulkPreview.unmatched}</p>
                      <p className="text-[10px] text-muted-foreground uppercase">Unmatched</p>
                    </div>
                  </div>

                  {bulkPreview.customers_affected?.length > 0 && (
                    <div className="text-xs">
                      <span className="font-medium">Customers affected: </span>
                      <span className="text-muted-foreground">{bulkPreview.customers_affected.join(', ')}</span>
                    </div>
                  )}

                  {bulkPreview.matched > 0 && (
                    <div className="max-h-48 overflow-y-auto border rounded-md">
                      <table className="w-full text-[11px]">
                        <thead className="bg-muted/40 sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-semibold">Bill ID</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Customer</th>
                            <th className="px-2 py-1.5 text-right font-semibold">Amount</th>
                            <th className="px-2 py-1.5 text-right font-semibold">Fee</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Ref</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkPreview.matched_details?.map((m, i) => (
                            <tr key={i} className="border-t border-border/30">
                              <td className="px-2 py-1 font-mono truncate max-w-[120px]" title={m.bill_id}>{m.bill_id.slice(0, 8)}...</td>
                              <td className="px-2 py-1 truncate max-w-[120px]">{m.customer_name}</td>
                              <td className="px-2 py-1 text-right font-mono">{formatCurrency(m.new_amount)}</td>
                              <td className="px-2 py-1 text-right font-mono">{formatCurrency(m.service_fee)}</td>
                              <td className="px-2 py-1 truncate max-w-[80px]">{m.transaction_ref || '\u2014'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {bulkPreview.unmatched > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-red-600 font-medium">
                        {bulkPreview.unmatched} unmatched bills (click to expand)
                      </summary>
                      <div className="mt-1 max-h-32 overflow-y-auto p-2 bg-red-50 rounded text-[10px]">
                        {bulkPreview.unmatched_details?.map((u, i) => (
                          <p key={i} className="font-mono">{u.bill_id} — {u.reason}</p>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}

              {/* Commit Result */}
              {bulkResult && (
                <div className="p-4 rounded-lg border bg-emerald-50 space-y-2" data-testid="bulk-upload-result">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <p className="font-semibold text-emerald-700">Upload Complete</p>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center text-xs">
                    <div>
                      <p className="text-lg font-bold text-emerald-700">{bulkResult.success}</p>
                      <p className="text-muted-foreground">Recorded</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-amber-600">{bulkResult.skipped}</p>
                      <p className="text-muted-foreground">Skipped</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-red-600">{bulkResult.errors?.length || 0}</p>
                      <p className="text-muted-foreground">Errors</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setBulkUploadOpen(false)} data-testid="bulk-upload-cancel">
                {bulkResult ? 'Close' : 'Cancel'}
              </Button>
              {bulkPreview && !bulkResult && bulkPreview.matched > 0 && (
                <Button
                  onClick={handleBulkCommit}
                  disabled={bulkCommitting}
                  data-testid="bulk-upload-commit"
                >
                  {bulkCommitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : `Record ${bulkPreview.matched} Transactions`}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
      </TooltipProvider>
    </MainLayout>
  );
}
